'use client'
import * as React from 'react'
import { tw } from 'utils/classnames'
import { StarIcon } from 'primitives/icons/star'

interface RatingProps {
  id: string
  disabled?: boolean
  readOnly?: boolean
  value?: number
  className?: string
  onChange?: (value: number) => void
}

export function RatingInput({ id, value, disabled, readOnly, onChange, className }: RatingProps) {
  const [hoverIndex, setHoverIndex] = React.useState(0)
  const maxCheckedStar = Math.floor(value || 0)

  function onRatingChange(
    event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLInputElement>,
  ) {
    const target = event.target as HTMLInputElement
    // oxlint-disable-next-line radix
    const value = parseInt(target.value)
    onChange?.(value)
  }

  return (
    <div className={tw('flex', className)}>
      {[1, 2, 3, 4, 5].map(starIndex => {
        return (
          <Star
            required
            className="h-full"
            key={starIndex}
            id={`rating-${id}-${starIndex}`}
            name="rating"
            value={starIndex}
            disabled={disabled}
            readOnly={readOnly}
            checked={starIndex <= maxCheckedStar}
            onChange={onRatingChange}
            onClick={onRatingChange}
            onMouseEnter={() => setHoverIndex(starIndex)}
            onMouseLeave={() => setHoverIndex(0)}
            isHover={starIndex <= hoverIndex}
          />
        )
      })}
    </div>
  )
}

export interface StarProps extends React.HTMLProps<HTMLInputElement> {
  isHover?: boolean
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

export function Star({
  className,
  disabled,
  readOnly,
  checked,
  isHover,
  id,
  onMouseEnter,
  onMouseLeave,
  ...rest
}: StarProps) {
  return (
    <label
      className={tw(
        'flex aspect-square items-center',
        !disabled && !readOnly && 'cursor-pointer',
        className,
      )}
      tabIndex={0}
      htmlFor={id}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <input
        id={id}
        type="radio"
        disabled={disabled || readOnly}
        readOnly={readOnly}
        checked={checked}
        className="sr-only"
        {...rest}
      />
      <StarIcon
        className={tw(
          'size-full',
          checked && !disabled ? 'text-[#F9A050]' : 'text-cf-dreamy-cloud-700',
          'transition-transform ease-out ',
          !disabled && !readOnly && 'md:hover:-translate-y-1',
          isHover && 'text-[#F9A050]/70',
        )}
        checked={checked || isHover}
      />
    </label>
  )
}
